/**
 * The bee sticker artwork used by the sticker generator and by the printable
 * child report. Order matters: StickersPage addresses these by index.
 */
export const STICKER_IMAGES = [
  "/images/stickers/bee-regular.png",
  "/images/stickers/bee-blush.png",
  "/images/stickers/bee-hair.png",
  "/images/stickers/bee-sick.png",
  "/images/stickers/bee-smart.png",
  "/images/stickers/bee-antennae.png",
  "/images/stickers/bee-pirate.png",
  "/images/stickers/bee-chef.png",
  "/images/stickers/bee-space.png",
  "/images/stickers/bee-painter.png",
  "/images/stickers/bee-hero.png",
  "/images/stickers/bee-cowboy.png",
  "/images/stickers/bee-scary.png",
  "/images/stickers/bee-merry.png",
  "/images/stickers/bee-easter.png",
  "/images/stickers/bee-love.png",
  "/images/stickers/bee-study.png",
  "/images/stickers/bee-swim.png",
  "/images/stickers/bee-magic.png",
  "/images/stickers/bee-witch.png",
  "/images/stickers/bee-pumpkin.png",
  "/images/stickers/bee-vampire.png",
];

/**
 * Some of the sticker PNGs have an opaque white background painted in rather
 * than transparency, which shows as a white slab on a coloured panel. The
 * sticker tool never hit this because the artwork fills the whole cell.
 * Multiply blending drops white to the backdrop and leaves the bee untouched --
 * safe here because every report surface behind a sticker is light.
 */
export const STICKER_BLEND = "multiply" as const;

/** Stickers that would look wrong on a celebratory progress report. */
const REPORT_EXCLUDED = ["bee-sick.png", "bee-scary.png"];

/** The subset used to decorate reports. */
export const REPORT_STICKER_IMAGES = STICKER_IMAGES.filter(
  (src) => !REPORT_EXCLUDED.some((excluded) => src.endsWith(excluded))
);

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

/** Small seeded PRNG, so a given child's report always looks the same. */
const mulberry32 = (seed: number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), a | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export interface DecorativeSticker {
  src: string;
  /** Degrees of tilt, so a row of stickers does not look mechanical. */
  rotate: number;
}

/**
 * A pseudo-random run of stickers, distinct until the pool is exhausted.
 * Deterministic for a given seed, so editing the summary or re-rendering never
 * reshuffles the decoration -- and the printout matches the screen.
 */
export const pickStickers = (seed: string, count: number): DecorativeSticker[] => {
  const random = mulberry32(hashString(seed));
  const pool = [...REPORT_STICKER_IMAGES];

  // Seeded Fisher-Yates.
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  return Array.from({ length: count }, (_, i) => ({
    src: pool[i % pool.length],
    rotate: Math.round((random() * 2 - 1) * 18),
  }));
};

export interface GutterPlacement extends DecorativeSticker {
  size: number;
  /** Vertical alignment within the row, for a less regimented look. */
  align: "flex-start" | "center" | "flex-end";
  opacity: number;
}

export interface GutterRow {
  left: GutterPlacement | null;
  right: GutterPlacement | null;
}

/**
 * Bees for the margins either side of each observation card.
 *
 * These are deliberately in-flow rather than absolutely positioned: an
 * absolute layer is clipped at the first page break, so anything past page one
 * would vanish from the printout. A gutter column per row also guarantees the
 * margin the bees sit in, on screen and on paper alike.
 */
export const gutterStickers = (seed: string, rows: number): GutterRow[] => {
  const random = mulberry32(hashString(`${seed}-gutter`));
  // Up to two bees per row, so draw a generous pool.
  const pool = pickStickers(`${seed}-gutter`, rows * 2);
  const aligns: GutterPlacement["align"][] = ["flex-start", "center", "flex-end"];
  let taken = 0;

  const place = (): GutterPlacement => {
    const sticker = pool[taken++ % pool.length];
    return {
      ...sticker,
      size: Math.round(58 + random() * 30),
      align: aligns[Math.floor(random() * aligns.length)],
      opacity: 0.7 + random() * 0.3,
    };
  };

  return Array.from({ length: rows }, (_, i) => {
    const roll = random();
    // Mostly alternate sides; occasionally both, occasionally neither, so the
    // margins do not read as a rigid column of bees.
    const both = roll > 0.82;
    const neither = roll < 0.12;
    if (neither) return { left: null, right: null };
    if (both) return { left: place(), right: place() };
    return i % 2 === 0 ? { left: place(), right: null } : { left: null, right: place() };
  });
};
